import { useEffect, useState } from 'react';
import { Select, styled, MenuItem } from '@mui/material';
import {
  activityStatusColorMap,
  activityStatusLabel,
} from '@/constants/idp/activity-status';
import { useUpdatePlanActivityStatus } from '@/data/idp/plans/activities/update-status';
import {
  type Activity,
  ActivityStatus,
  PlanStatus,
} from '@/data/idp/plans/interfaces';
import { fontFamily } from '@/@mui/tokens/font-family';
import { fontWeights } from '@/@mui/tokens/font-weights';

type StyledSelectProps = {
  activityStatus: ActivityStatus;
};

type ActivityStatusSelectProps = {
  activity: Activity;
  planStatus?: PlanStatus;
};

const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== 'activityStatus',
})<StyledSelectProps>(({ theme, activityStatus }) => ({
  backgroundColor:
    activityStatus === ActivityStatus.Pending
      ? 'transparent'
      : activityStatusColorMap[activityStatus].backgroundColor,
  color: activityStatusColorMap[activityStatus].color,
  fontSize: theme.typography.body2.fontSize,
  fontFamily: fontFamily.fontFamilyCarbona,
  fontWeight: fontWeights.fontWeightSemibold,
  borderRadius: theme.shape.borderRadius,
  '& .MuiSelect-select': {
    padding: theme.spacing(0, 1),
  },
  '& .MuiSelect-icon': {
    color: activityStatusColorMap[activityStatus].color,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    ...(activityStatus !== ActivityStatus.Pending && {
      border: 'none',
    }),
  },
}));

const statusOptions = Object.entries(activityStatusLabel).map(
  ([value, label]) => ({
    value,
    label,
  })
);

export const ActivityStatusSelect = ({
  activity,
  planStatus,
}: ActivityStatusSelectProps) => {
  const { mutate: updatePlanActivity } = useUpdatePlanActivityStatus(
    activity.plan_id
  );
  const [activityStatus, setActivityStatus] = useState<ActivityStatus>(
    activity.status
  );

  const handleChangeStatus = (status: ActivityStatus) => {
    updatePlanActivity(
      { activityId: activity.id, status },
      {
        onSuccess: () => setActivityStatus(status),
      }
    );
  };

  useEffect(() => {
    if (activity.status) {
      setActivityStatus(activity.status);
    }
  }, [activity]);

  return (
    <StyledSelect
      activityStatus={activityStatus}
      disabled={planStatus === PlanStatus.Closed}
      onChange={(event) =>
        handleChangeStatus(event.target.value as ActivityStatus)
      }
      renderValue={(selected) =>
        activityStatusLabel[selected as ActivityStatus]
      }
      size="small"
      value={activityStatus}
      onClick={(event) => event.stopPropagation()}
    >
      {statusOptions.map(({ label, value }) => (
        <MenuItem
          key={value}
          value={value}
          onClick={(event) => event.stopPropagation()}
        >
          {label}
        </MenuItem>
      ))}
    </StyledSelect>
  );
};
